/**
 * `<FontColorPicker>` — the rich-text font-colour control.
 *
 * Replaces the bare native `<input type="color">` (whose popup is OS-chrome,
 * off-design) with an in-app popover that follows the app's design language:
 * a curated preset palette, a "recent" row backed by `useUiStore`
 * (`recentEditorColors`, persisted, capped at 8), a Custom… affordance (the
 * native picker, for arbitrary colours), and a Clear action. Applying any
 * colour records it in the recents list.
 */

import { useEffect, useRef, useState } from 'react';
import type { Editor } from '@tiptap/react';
import { useUiStore } from '@/stores';

interface FontColorPickerProps {
  editor: Editor;
}

/** Curated palette — neutrals + the app's accent family, all legible on dark. */
const PRESET_COLORS: readonly string[] = [
  '#ffffff',
  '#c9d1d9',
  '#8b949e',
  '#6e7681',
  '#ff7b39',
  '#f0b429',
  '#e5484d',
  '#ec4899',
  '#3fb950',
  '#39c5cf',
  '#58a6ff',
  '#a371f7',
];

export function FontColorPicker({ editor }: FontColorPickerProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const recents = useUiStore((s) => s.recentEditorColors);
  const pushRecent = useUiStore((s) => s.pushRecentEditorColor);

  const currentColor = editor.getAttributes('textStyle').color as
    | string
    | undefined;

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e: MouseEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const apply = (color: string): void => {
    editor.chain().focus().setColor(color).run();
    pushRecent(color);
    setOpen(false);
  };

  const clear = (): void => {
    editor.chain().focus().unsetColor().run();
    setOpen(false);
  };

  return (
    <div className="rich-color" ref={wrapRef}>
      <button
        type="button"
        className="rich-toolbar__btn rich-color__trigger"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen((o) => !o)}
        title="Font color"
        aria-label="Font color"
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className="rich-color__glyph">A</span>
        <span
          className="rich-color__bar"
          style={{ background: currentColor ?? 'var(--fg)' }}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div className="rich-color__pop" role="dialog" aria-label="Font color">
          <div className="rich-color__label">Palette</div>
          <div className="rich-color__grid">
            {PRESET_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                className={
                  'rich-color__swatch' +
                  (currentColor?.toLowerCase() === c
                    ? ' rich-color__swatch--active'
                    : '')
                }
                style={{ background: c }}
                title={c}
                aria-label={`Set color ${c}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => apply(c)}
              />
            ))}
          </div>

          {recents.length > 0 && (
            <>
              <div className="rich-color__label">Recent</div>
              <div className="rich-color__grid">
                {recents.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={
                      'rich-color__swatch' +
                      (currentColor?.toLowerCase() === c.toLowerCase()
                        ? ' rich-color__swatch--active'
                        : '')
                    }
                    style={{ background: c }}
                    title={c}
                    aria-label={`Set color ${c}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => apply(c)}
                  />
                ))}
              </div>
            </>
          )}

          <div className="rich-color__footer">
            <label
              className="rich-color__custom"
              onMouseDown={(e) => e.preventDefault()}
            >
              <span
                className="rich-color__custom-swatch"
                style={{ background: currentColor ?? 'var(--fg)' }}
                aria-hidden="true"
              />
              <span>Custom…</span>
              <input
                type="color"
                aria-label="Custom font color"
                value={currentColor ?? '#ffffff'}
                onChange={(e) => apply(e.target.value)}
              />
            </label>
            <button
              type="button"
              className="rich-color__clear"
              onMouseDown={(e) => e.preventDefault()}
              onClick={clear}
              disabled={currentColor === undefined}
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
